import "@/App.css";
import "@/kit.css";
import "@/kit-site.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import Founder from "@/pages/Founder";
import StandingOrders from "@/pages/StandingOrders";
import { CartProvider } from "@/context/CartContext";
import { AuthProvider } from "@/context/AuthContext";
import { SiteProvider } from "@/context/SiteContext";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { AuthCallback } from "@/components/AuthCallback";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductPage from "@/pages/ProductPage";
import FindYourJar from "@/pages/FindYourJar";
import SeniorDogs from "@/pages/SeniorDogs";
import OurStory from "@/pages/OurStory";
import OurStandard from "@/pages/OurStandard";
import Help from "@/pages/Help";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";
import Policy from "@/pages/Policy";
import Keeper from "@/pages/Keeper";
import Journal from "@/pages/Journal";
import Article from "@/pages/Article";
import Account from "@/pages/Account";
import HowItWent from "@/pages/HowItWent";
import BatchPage from "@/pages/BatchPage";
import { ChatWidget } from "@/components/ChatWidget";
import { SignupDialog } from "@/components/Correspondence";
import { PRODUCTS } from "@/data/catalog";

const REDIRECTS = [
  ["/origins", "/our-story"], ["/gray-muzzle", "/senior-dogs"], ["/standard", "/our-standard"], ["/ledger", "/our-standard"],
  ["/measure", "/help#how-to-use"], ["/faq", "/help#questions"], ["/wild-index", "/journal"], ["/quiz", "/find-your-jar"], ["/find-their-mushroom", "/find-your-jar"],
];

const PAGES = [
  ["/shop", <Shop />], ["/products/:slug", <ProductPage />], ["/find-your-jar", <FindYourJar />], ["/senior-dogs", <SeniorDogs />],
  ["/our-story", <OurStory />], ["/founders/:slug", <Founder />], ["/our-standard", <OurStandard />], ["/help", <Help />],
  ["/standing-orders", <StandingOrders />], ["/policies/:slug", <Policy />], ["/journal", <Journal />], ["/journal/:slug", <Article />],
  ["/account", <Account />], ["/batch/:batchNo", <BatchPage />],
];

const ShopSlugRedirect = ({ fr }) => {
  const { slug } = useParams();
  return <Navigate to={`${fr}/products/${slug}`} replace />;
};

/* /fr/... addresses show the same pages in French. */
const LangFromPath = () => {
  const { pathname } = useLocation();
  const { lang, setLang } = useLang();
  const want = pathname === "/fr" || pathname.startsWith("/fr/") ? "fr" : "en";
  useEffect(() => { if (lang !== want) setLang(want); }, [want, lang, setLang]);
  return null;
};

const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) { window.scrollTo(0, 0); return undefined; }
    const id = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ block: "start" }), 120);
    return () => clearTimeout(id);
  }, [pathname, hash]);
  return null;
};

/* Old review-request emails link to /?review=<jar>&email=… ; send them to the jar's page. */
const HomeOrReview = () => {
  const params = new URLSearchParams(window.location.search);
  const p = PRODUCTS.find((x) => x.id === params.get("review"));
  if (p) return <Navigate to={`/products/${p.slug}?email=${encodeURIComponent(params.get("email") || "")}`} replace />;
  return <Home />;
};

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
      <ScrollManager />
      <LangFromPath />
      <Routes>
        <Route path="/" element={<HomeOrReview />} />
        <Route path="/fr" element={<Home />} />
        {["", "/fr"].flatMap((fr) => [
          ...PAGES.map(([path, el]) => <Route key={fr + path} path={fr + path} element={el} />),
          ...REDIRECTS.map(([from, to]) => <Route key={fr + from} path={fr + from} element={<Navigate to={fr + to} replace />} />),
          <Route key={`${fr}shopslug`} path={`${fr}/shop/:slug`} element={<ShopSlugRedirect fr={fr} />} />,
        ])}
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/keeper" element={<Keeper />} />
        <Route path="/how-it-went/:token" element={<HowItWent />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <ChatWidget />
      <SignupDialog />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <LanguageProvider>
      <AuthProvider>
      <CartProvider>
      <SiteProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
      </SiteProvider>
      </CartProvider>
      </AuthProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
